import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { FilterCommonType, FilterKeys } from '../../types';
import useFilterCond from '../../hooks/useFilterCond';
import { FilterItemClassName, defaultFilterValue } from '../../constants';
import useDefaultFilter from '../../hooks/useDefaultFilter';
import { defaultFilter } from '../../constants/filterOptions';
import { setSessionStorage } from '../../utils/storage';
import useDependencyCollection from '../../hooks/useDependencyCollection';
import {
  finallySpecialValue,
  getTimeLimitation,
  isTimeLimitation,
} from '../../utils';
import { dealwithCacheFilter, setShortUrl } from '../../utils/historyUtils';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
import { FilterGroups } from './RenderFilterList';
import _ from 'lodash-es';
import { useFrontWebConfig } from '@op/hooks/useFrontWebConfig';

export default function useRenderComponent({
  filter = defaultFilter,
  renderExcludeFilter,
  lang,
  confirm,
  setFilterSelect,
  defaultConfig,
  defaultValue,
  userSynchronousValue,
  hasServerParams = false,
  gameInfo,
  wholeGameInfo,
  userInfo,
  selectDefaultOptions = {},
  backCondOriginData,
  dealwithFilterOptionsMap = {},
  finallyDealwithDefaultValue,
  beforeCacheDealwithData = (cache: any) => cache,
  dealwithFinallyCondData,
  setConfirmDisable,
  noLimitation = false,
  filterItemRender,
  otherRequestType,
  isNoRequest,
  externalCondData,
  finallyDealwithResetValue,
  filterName,
  changeOnCloseWindow,
  isTile = false,
  shortid,
  maximumDuration,
  dynamicsConfig,
  inheritStorageFilterValue,
  noPreviewsGroups,
}: Omit<FilterCommonType, 'historyConfig'> & {
  gameInfo: any;
  wholeGameInfo: any;
  userInfo: any;
  filterName?: string;
  setConfirmDisable?: (value: boolean) => void;
  filterItemRender?: (
    filterKey: FilterKeys,
    element: JSX.Element,
  ) => JSX.Element;
}) {
  const {
    defaultFilter,
    loading: defaultLoading,
    oriDefaultFilter,
    backOriDefaultValue,
  } = useDefaultFilter({
    defaultValue,
    gameInfo,
    userInfo,
    hasServerParams,
    finallyDealwithDefaultValue,
    shortid,
    inheritStorageFilterValue,
  });

  const filterStateValueRef = useRef<any>({});
  const [filterStateValue, setFilterStateValue] = useState<{
    [key: string]: any;
  }>({});

  const { filterOptions, loading: condLoading } = useFilterCond({
    filter,
    gameInfo,
    userInfo,
    defaultOptions: selectDefaultOptions,
    backCondOriginData,
    otherRequestType,
    isNoRequest,
    externalCondData,
    dealwithFinallyCondData,
  });

  const loading = useMemo(
    () => condLoading || defaultLoading,
    [condLoading, defaultLoading],
  );

  /** 最终返回给前台的数据 */
  const filterResult = useRef<{ [key: string]: any }>({});

  const { triggerCollectionCallback, collectionCallback } =
    useDependencyCollection();

  // 初始化标记
  const init = useRef(true);
  // 上锁标记,上锁后,不允许onChange改filterName
  const lock = useRef(true);
  const timer: any = useRef(null);
  const onChange = (
    key: string,
    value: any,
    deleteKeys?: string | Array<string>,
  ) => {
    // TODO 这里要进行数据深度比较，然后相同数据不进入
    const data = filterResult.current[key];

    // 有些时候需要删除一些数据
    if (deleteKeys) {
      const processDeleteKeys =
        typeof deleteKeys === 'string' ? [deleteKeys] : deleteKeys;
      processDeleteKeys.forEach((item) => {
        if (Object.prototype.hasOwnProperty.call(data || {}, item)) {
          delete data[item];
        }
      });
    }
    filterResult.current[key] = { ...data, ...value };
    triggerCollectionCallback(key as any, value);
    // 初始化，将数据冒出去
    if (init.current) {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => {
        // 初始化完成，并且解除设置名称的锁
        init.current = false;
        lock.current = false;
        confirm(dealwithBackData(true), true);
      }, 300);
    } else {
      // 实时更新header数据
      confirm(dealwithBackData(true));
    }
    // 修改就设置filterName为空
    if (!lock.current) {
      setFilterSelect?.();
    }
  };
  const resetForm = async (data?: { [key: string]: any }) => {
    let value = data || oriDefaultFilter;
    const callbackValue = await finallyDealwithResetValue?.(value);
    if (callbackValue) {
      value = callbackValue;
    }
    filterStateValueRef.current = _.cloneDeep(value);
    setFilterStateValue(_.cloneDeep(value));
  };
  /** history组件切换保存的选项 */
  const historyChange = useCallback(
    (filterItem: any, name: string) => {
      console.log(filterItem, name, 'historyChange');
      lock.current = true;
      if (filterItem) {
        filterStateValueRef.current = {
          ...defaultFilterValue(),
          ...filterItem,
        };
        setFilterStateValue(filterItem);
      }
      // 设置过滤器选中名称
      setFilterSelect?.(name);
      // 设置过滤器缓存，不然切换页面回显名称时，过滤器相关设置不匹配
      setCacheInfo(true, filterItem);
      setTimeout(() => {
        lock.current = false;
      }, 1000);
    },
    [userInfo, gameInfo],
  );

  /**
   * 过滤部分用户所同步的值
   * 比如说一开始不准备同步【使用缓存中的数据作为默认值】，后期同步的情况
   *
   * 标志为是否值为null
   * @param data
   */
  const filterUserSynchronousValue = (data: { [key: string]: any } = {}) => {
    if (filterName) return {};

    return _.pickBy(data, (value: null) => value !== null);
  };

  // 数据来源于：/global/page_source_rule
  // 因为只有无权限的数据返回
  // 无配置数据就有两个意思：
  //   配置单游戏白名单【A】资源
  //   其他游戏没有配置【A】资源为黑名单
  //  所以这里借用黑名单当作白名单使用: in_key_kol | in_sentiment_clause
  const { excludeFilter, includeFilter } = useGetSingePageSourceRule({});
  // 数据来源于：font_web_config
  const default_no_show_filter = useFrontWebConfig({
    keys: 'default_no_show_filter',
  });

  /** 渲染组件 */
  const renderComponentElementFunc = useCallback(() => {
    if (loading) return null;

    let localFilter = [...filter];
    // 排除掉不想渲染的过滤器
    if (renderExcludeFilter) {
      localFilter = localFilter.filter(
        (item) => !renderExcludeFilter.includes(item),
      );
    }
    const realUserSynchronousValue = filterUserSynchronousValue(
      userSynchronousValue || {},
    );

    const params: { [key: string]: any } = {
      onChange,
      confirm: historyChange,
      changeOnCloseWindow,
      filterValue: Object.assign(
        {},
        filterStateValueRef.current,
        realUserSynchronousValue,
      ),
      defaultValue: defaultFilter,
      oriDefaultFilter,
      isMobile: gameInfo?.entity_type === 'mobile',
      maximumDuration,
      lang,
      gameInfo,
      wholeGameInfo,
      noLimitation,
      collectionCallback,
      setConfirmDisable,
      backOriDefaultValue,
      dynamicsConfig,
    };

    if (!filterOptions.reviewType) {
      localFilter = localFilter.filter((item) => item !== 'liveVenues');
    }

    // 部分用户没有办法加载的过滤器去掉
    if (excludeFilter?.length > 0) {
      localFilter = localFilter.filter((item) => !excludeFilter.includes(item));
    }

    // 排除掉默认不需要展示的过滤器
    if (default_no_show_filter?.length > 0) {
      localFilter = localFilter.filter((item) => {
        if (default_no_show_filter.includes(item)) {
          return includeFilter.includes(item);
        }
        return true;
      });
    }
    // console.log("localFilter", "-----------********-------------")
    // const FilterListComponent = FilterGroups;
    // isTile ? FilterTile :
    return (
      <FilterGroups
        isTile={isTile}
        filter={localFilter}
        filterOptions={filterOptions}
        params={params}
        dealwithFilterOptionsMap={dealwithFilterOptionsMap}
        FilterItemClassName={FilterItemClassName}
        defaultConfig={defaultConfig}
        filterItemRender={filterItemRender}
        noPreviewsGroups={noPreviewsGroups}
      ></FilterGroups>
    );
  }, [
    renderExcludeFilter,
    loading,
    filterStateValue,
    filterOptions,
    lang,
    userSynchronousValue,
    maximumDuration,
    dynamicsConfig,
  ]);

  // useEffect(() => {
  //   console.log('renderExcludeFilter');
  // }, [renderExcludeFilter]);
  // useEffect(() => {
  //   console.log('loading');
  // }, [loading]);
  // useEffect(() => {
  //   console.log('filterStateValue');
  // }, [filterStateValue]);
  // useEffect(() => {
  //   console.log('lang');
  // }, [lang]);
  // useEffect(() => {
  //   console.log('userSynchronousValue');
  // }, [userSynchronousValue]);

  const renderComponentElement = useMemo(() => {
    return renderComponentElementFunc();
  }, [renderComponentElementFunc]);

  const userInfoRef = useRef({});
  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);
  const setShortUrlInfo = (noCache: boolean, filterValue: any) => {
    if (noCache) return;
    const { originValue, value, flag } = dealwithCacheFilter(
      filterValue,
      beforeCacheDealwithData,
    );
    // 如果不缓存就直接返回。也不用改变路由
    if (!flag) {
      return;
    }
    const { start_time, end_time } = originValue;
    if (
      isTimeLimitation(noLimitation, userInfoRef.current) &&
      getTimeLimitation([start_time, end_time])
    ) {
      return;
    }

    setShortUrl(value, gameInfo);
  };

  const setCacheInfo = (noCache: boolean, filterValue: any) => {
    if (noCache) return;
    const { flag, value } = dealwithCacheFilter(
      filterValue,
      beforeCacheDealwithData,
    );
    if (flag) {
      // 添加缓存
      setSessionStorage(value, gameInfo);
    }
  };

  // 处理返回数据，也是 过滤器“确认”按钮触发事件
  const dealwithBackData = (noCache = false) => {
    const localFilterResult = filterResult.current;
    const fixedHeaderShow = {};
    const filterValue: any = {};
    _.forOwn(
      localFilterResult,
      ({ fixedHeader, value }: { fixedHeader: any; value: any }) => {
        _.assign(filterValue, value);
        _.assign(fixedHeaderShow, fixedHeader);
      },
    );

    filterStateValueRef.current = Object.assign(
      filterStateValueRef.current,
      filterValue,
    );

    setCacheInfo(noCache, filterValue);
    setShortUrlInfo(noCache, filterValue);

    return {
      value: finallySpecialValue(filterValue),
      oriFilterValue: filterValue,
      fixedHeader: fixedHeaderShow,
    };
  };

  return {
    renderComponentElement,
    resetForm,
    dealwithBackData,
    historyChange,
    loading,
  };
}
